import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Modal, Spin } from "antd";
import { EngageColumns } from "./Columns";
import {
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { GlobalTable } from "../../../global/components/GlobalTable";
import usePostEngageLogin from "../../../global/hooks/usePostEngageLogin";
import { getEnageUser } from "../../../global/hooks/getUserRole";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import EngageModalForm from "../Components/EngageModalForm";

export const Engage = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isEngageModalForm, setEngageModalForm] = useState(false);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "SiteReference",
    order: "asc",
  }); //

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [type, setType] = useState(null);
  const [defaultValue, setDefaultValue] = useState(null);

  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      label: "Engage Site",
      href: "/engagesitelist",
    },
  ];
  const postUrl = `${process.env.REACT_APP_API_URL}/api/engage/get-sites`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/engage/delete-site`;

  const user = getEnageUser();
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePostEngageLogin(postUrl);
  const {
    data: deleteData,
    loading: deleteLoading,
    error: deleteError,
    postData: deleteAction,
  } = usePostEngageLogin(deleteUrl);

  const fetchData = async () => {
    if (user?.username && user?.password) {
      const encryptedValues = {
        UserName: user?.username,
        PassWord: user?.password,
      };
      await postAction(encryptedValues);
    }
  };

  useEffect(() => {
    if (!postData && !postLoading) {
      fetchData();
    }
  }, []);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // navigate(`/oemlist/${newSelectedRowKeys}`);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record, rowIndex) => {
    return {
      onClick: (event) => {
        localStorage?.setItem(
          "engageSiteData",
          JSON.stringify({
            SiteReference: record?.SiteReference,
            SiteName: record?.Name,
          })
        );
        navigate(`/engagesite/info/${record?.SiteReference}`);
      },
    };
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length > 0) {
        const body = {
          UserName: user?.username,
          PassWord: user?.password,
          SiteReference: selectedRowKeys,
        };
        await deleteAction(body);
        if (!deleteLoading) {
          setSelectedRowKeys([]);
        }
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  useEffect(() => {
    if (deleteData && deleteData?.status === 200) {
      openConfirmNotification("Success");
      fetchData();
    } else if (deleteError) {
      openErrorNotification("Something went wrong.");
    }
  }, [deleteData]);

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <div className="mb-4">
          <div className="w-full overflow-x-auto">
            <div className="flex justify-between flex-nowrap min-w-[610px]">
              <div className="flex items-center gap-0">
                <Button
                  type="primary"
                  className="mr-2  w-[104px] h-[30px] font-medium"
                  onClick={() => {
                    setEngageModalForm(true);
                    setType("Add");
                    setDefaultValue(null);
                    setSelectedRowKeys([]);
                  }}
                >
                  <PlusOutlined />
                  Add
                </Button>
                {deleteLoading ? (
                  <Button
                    type="primary"
                    disabled={selectedRowKeys?.length === 0 ? true : false}
                    className="mr-2 w-[104px] h-[30px] font-medium"
                  >
                    <Spin
                      indicator={
                        <LoadingOutlined
                          spin
                          className="h-4 w-4"
                          style={{ color: "white" }}
                        />
                      }
                    />
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    disabled={selectedRowKeys?.length === 0 ? true : false}
                    className="mr-2 w-[104px] h-[30px] font-medium"
                    onClick={() => {
                      setShowModal(true);
                    }}
                  >
                    <DeleteOutlined />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          onDeleteConfirm={onDeleteConfirm}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <Modal
          open={isEngageModalForm}
          onCancel={() => setEngageModalForm(false)}
          closeIcon={false}
          footer={null}
          width={580}
          centered
          motion={false}
          title={
            <div className="flex items-center bg-gray-200 p-2 rounded-md">
              <span className="text-[14px] font-[Poppins] text-black  ml-2">
                {type} Engage Site
              </span>
            </div>
          }
        >
          <EngageModalForm
            setEngageModalForm={setEngageModalForm}
            type={type}
            fetchData={fetchData}
            defaultValue={defaultValue}
          />
        </Modal>
        <GlobalTable
          columns={EngageColumns(
            hoveredRow,
            setShowModal,
            setEngageModalForm,
            setType,
            setDefaultValue,
            setSelectedRowKeys,
            setParams,
            params
          )}
          dataSource={
            postData?.data?.data?.length > 0
              ? postData?.data?.data?.map((item, index) => ({
                  ...item,
                  key: item?.SiteReference,
                  id: index,
                }))
              : []
          }
          rowSelection={rowSelection}
          // pagination={paginationConfig}
          onRow={(record, rowIndex) => {
            return {
              onMouseEnter: () => setHoveredRow(rowIndex),
              onMouseLeave: () => setHoveredRow(null),
              ...onRow?.(record, rowIndex),
            };
          }}
          loading={postLoading}
        />
      </div>
    </div>
  );
};
